import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { CollectionOptions } from './CollectionSelect';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Folder, FolderOpen, Pointer, Repeat, Mic, Headphones, Play } from 'lucide-react';

/**
 * Shared "Start Practice Session" dialog used by both the Dashboard and
 * Collections pages so there is a single source of truth for this UI.
 */
const PracticeSessionDialog = ({
    open,
    onOpenChange,
    collections = [],
    totalPhrases = 0,
    selectedPracticeCollections = [],
    setSelectedPracticeCollections,
    practiceFlow,
    setPracticeFlow,
    practiceMode,
    setPracticeMode,
    onStart,
}) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="practice-selection-description">
                <DialogHeader>
                    <DialogTitle>Start Practice Session</DialogTitle>
                </DialogHeader>
                <p id="practice-selection-description" className="sr-only">
                    Select a collection and mode to start a practice session.
                </p>
                <div className="space-y-2">
                    <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-stone-700">Choose a collection of phrases</Label>
                        <Select
                            value={selectedPracticeCollections[0] || 'all'}
                            onValueChange={(v) => setSelectedPracticeCollections(v === 'all' ? [] : [v])}
                        >
                            <SelectTrigger data-testid="practice-collection-select" className="bg-stone-50">
                                <SelectValue placeholder="All Phrases" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    <div className="flex items-center gap-2">
                                        <Folder className="h-4 w-4 text-primary" />
                                        All Phrases ({totalPhrases})
                                    </div>
                                </SelectItem>
                                <CollectionOptions>
                                    {collections.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            <div className="flex items-center gap-2">
                                                <FolderOpen className="h-4 w-4 text-stone-400" />
                                                {c.name} ({c.phrase_count})
                                            </div>
                                        </SelectItem>
                                    ))}
                                </CollectionOptions>
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="space-y-1.5">
                        <Label className="text-sm font-semibold text-stone-700">Mode</Label>
                        <div className="grid grid-cols-2 gap-2 items-stretch">
                            <button
                                data-testid="flow-manual"
                                type="button"
                                className={`p-3 rounded-xl border-2 text-left transition-all ${practiceFlow === 'manual' ? 'border-primary bg-primary/5' : 'border-stone-200 hover:border-stone-300'}`}
                                onClick={() => setPracticeFlow('manual')}
                            >
                                <span className={`flex items-center gap-2 text-sm font-medium ${practiceFlow === 'manual' ? 'text-primary' : 'text-stone-700'}`}>
                                    <Pointer className="h-4 w-4" />
                                    Manual
                                </span>
                            </button>
                            <Select
                                value={practiceFlow === 'automatic' ? practiceMode : ''}
                                onValueChange={(v) => { setPracticeMode(v); setPracticeFlow('automatic'); }}
                            >
                                <SelectTrigger
                                    data-testid="flow-automatic"
                                    className={`h-auto p-3 rounded-xl border-2 text-sm font-medium transition-all ${practiceFlow === 'automatic' ? 'border-accent bg-accent/5 text-accent' : 'border-stone-200 hover:border-stone-300 text-stone-700'}`}
                                >
                                    <span className="!flex flex-row items-center gap-2 text-left">
                                        <Repeat className="h-4 w-4 shrink-0" />
                                        <span className="flex flex-col leading-tight">
                                            <span>Auto</span>
                                            {practiceFlow === 'automatic' && (
                                                <span className="text-xs font-normal text-stone-400">
                                                    {practiceMode === 'listen-record'
                                                        ? 'Listen, Repeat & Analysis'
                                                        : practiceMode === 'listen-repeat'
                                                        ? 'Listen & Repeat'
                                                        : 'Listen Only'}
                                                </span>
                                            )}
                                        </span>
                                    </span>
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="listen-record">
                                        <div className="flex items-center gap-2">
                                            <Repeat className="h-4 w-4 text-primary" />
                                            Listen, Repeat &amp; Analysis
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="listen-repeat">
                                        <div className="flex items-center gap-2">
                                            <Mic className="h-4 w-4 text-accent" />
                                            Listen &amp; Repeat
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="listen-only">
                                        <div className="flex items-center gap-2">
                                            <Headphones className="h-4 w-4 text-muted-foreground" />
                                            Listen Only
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <p className="text-xs text-stone-500 mt-1.5">
                            {practiceFlow === 'manual'
                                ? "One phrase at a time, you're in control."
                                : practiceMode === 'listen-record'
                                ? 'Auto-play, auto-record, auto-score. Completely hands-free.'
                                : practiceMode === 'listen-repeat'
                                ? 'Records your repetition and plays it back. No scoring.'
                                : 'Continuous playback only. No recording or pronunciation analysis.'}
                        </p>
                    </div>

                    <Button
                        data-testid="start-practice-selected-btn"
                        className="w-full rounded-full bg-accent hover:bg-accent/90 text-accent-foreground font-bold text-base px-7 py-3 h-auto mt-5"
                        onClick={onStart}
                    >
                        <Play className="h-5 w-5 mr-1" />
                        Start Session
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default PracticeSessionDialog;
